/**
 * @module components/modals/HelpModal
 * @summary Keyboard-shortcuts help dialog.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 584-619.
 */
import { useEffect } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SHORTCUTS: [string, string][] = [
  ['Select tool', 'V or Esc'],
  ['Connect tool', 'C'],
  ['Pan tool', 'H'],
  ['Pan canvas', 'Space + drag / middle-mouse'],
  ['Undo / Redo', 'Ctrl+Z / Ctrl+Shift+Z or Ctrl+Y'],
  ['Copy / Paste / Duplicate', 'Ctrl+C / Ctrl+V / Ctrl+D'],
  ['Select all', 'Ctrl+A'],
  ['Save JSON', 'Ctrl+S'],
  ['Group / Ungroup', 'Ctrl+G / Ctrl+Shift+G'],
  ['Delete', 'Del / Backspace'],
  ['Rotate 90°', 'R / Shift+R'],
  ['Lock / unlock', 'L'],
  ['Rename label', 'F2 / double-click'],
  ['Nudge', 'Arrows / Shift+Arrows'],
  ['Zoom', 'Mouse wheel / pinch / Ctrl+= / Ctrl+−'],
  ['Reset zoom', 'Ctrl+0'],
  ['Fit to screen', 'F'],
];

export function HelpModal({ open, onClose }: Props) {
  // Close on Escape (canvas shortcuts are suspended while the modal is open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 24,
          maxWidth: 460,
          width: '90vw',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 12px 48px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 16, marginBottom: 12, color: '#2b579a' }}>⌨ Keyboard Shortcuts</h2>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <tbody>
            {SHORTCUTS.map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '5px 8px', color: '#333' }}>{k}</td>
                <td style={{ padding: '5px 8px', textAlign: 'right', color: '#777', fontFamily: 'monospace' }}>
                  {v}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: '8px 18px',
            background: '#2b579a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
